import React, { startTransition, useCallback, useMemo, useState } from "react";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import clsx from "clsx";
import styles from "./Slider.module.scss";
import { SwiperOptions } from "swiper";
import { match } from "ts-pattern";
import {
  defaultLabels,
  SliderControls,
  SliderControlsLabels
} from "./SliderControls";
import { PaginationButton } from "../PaginationButton/PaginationButton";
import { BackgroundColor } from "../Backgrounds/background";
//import SwiperCore, { Lazy, SwiperOptions } from "swiper";
//SwiperCore.use([Lazy]);

export type SliderProps = {
  variantColor?: BackgroundColor;
  withMask?: boolean;
  withLeftOverflow?: boolean;
  labels?: SliderControlsLabels;
  onSlideChange?: (slideNumber: number) => void;
  suppressHydrationWarning?: boolean;
  loop?: boolean;
  hideNextSlideGroup?: boolean;
} & (
  | {
      variant?: "normal" | "wide" | "full" | "inline" | "compact";
      swiperConfig?: null;
    }
  | {
      variant: "custom";
      swiperConfig: Partial<SwiperOptions>;
    }
);

const SliderSlideBase: React.FC<
  React.PropsWithChildren<{ children: React.ReactNode }>
> = ({ children }) => {
  return <React.Fragment>{children}</React.Fragment>;
};
export const SliderSlide = React.memo(SliderSlideBase);

// const lazyOptions: SwiperOptions["lazy"] = {
//   checkInView: true,
//   loadPrevNext: true,
//   elementClass: "js-lazy-load",
//   loadOnTransitionStart: true
// };

const swiperConfigNormal: Partial<SwiperOptions> = {
  //lazy: lazyOptions,
  spaceBetween: 24,
  slidesPerView: 1,
  slidesPerGroup: 1,
  breakpoints: {
    //MD
    768: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 24
    },
    //LG
    1024: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 24
    },
    //XL
    1280: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 48
    }
  }
};

const swiperConfigCompact: Partial<SwiperOptions> = {
  //lazy: lazyOptions,
  spaceBetween: 24,
  slidesPerView: 1,
  slidesPerGroup: 1,
  breakpoints: {
    480: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 24
    },
    //MD
    768: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 24
    },
    //LG
    1024: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 24
    },
    //XL
    1280: {
      slidesPerView: 4,
      slidesPerGroup: 4,
      spaceBetween: 24
    }
  }
};

const swiperConfigInline: Partial<SwiperOptions> = {
  //lazy: lazyOptions,
  watchSlidesProgress: true,
  spaceBetween: 24,
  slidesPerView: 1,
  slidesPerGroup: 1,
  breakpoints: {
    //MD
    768: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    //LG
    1024: {
      slidesPerView: 3,
      slidesPerGroup: 3
    },
    //XL
    1280: {
      slidesPerView: 3,
      slidesPerGroup: 3
    }
  }
};

const swiperConfigWide = {
  //lazy: lazyOptions,
  spaceBetween: 24,
  slidesPerView: 1,
  slidesPerGroup: 1,
  breakpoints: {
    //MD
    768: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 24
    },
    //LG
    1024: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 24
    },
    //XL
    1280: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 48
    }
  }
};

const swiperConfigFull: Partial<SwiperOptions> = {
  //lazy: lazyOptions,
  spaceBetween: 24,
  slidesPerView: 1,
  slidesPerGroup: 1
};

const SliderBase: React.FC<React.PropsWithChildren<SliderProps>> = (props) => {
  const {
    children,
    variantColor = "neutral05",
    variant = "normal",
    withMask = true,
    withLeftOverflow = false,
    loop = false,
    labels,
    onSlideChange: onSlideChange,
    hideNextSlideGroup = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    swiperConfig,
    ...otherProps
  } = props;

  const swiperProps = match(props)
    .with({ variant: "normal" }, () => swiperConfigNormal)
    .with({ variant: "inline" }, () => swiperConfigInline)
    .with({ variant: "wide" }, () => swiperConfigWide)
    .with({ variant: "full" }, () => swiperConfigFull)
    .with({ variant: "compact" }, () => swiperConfigCompact)
    .with({ variant: "custom" }, (p) => p.swiperConfig)
    .otherwise(() => swiperConfigNormal);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesPerGroup, setSlidesPerGroup] = useState(1);
  const [swiper, setSwiper] = useState<SwiperClass>();

  const prevSlide = useCallback(() => {
    if (swiper) swiper.slidePrev();
  }, [swiper]);

  const nextSlide = useCallback(() => {
    if (swiper) swiper.slideNext();
  }, [swiper]);

  const setSwiperInstance = useCallback((instance: SwiperClass) => {
    startTransition(() => {
      setSwiper(instance);
    });
  }, []);

  const internalOnSlideChange = useCallback(
    (e) => {
      const current = Math.ceil(e.realIndex / (e.params.slidesPerGroup || 1));
      setCurrentSlide(current);
      if (onSlideChange) onSlideChange(current);
    },
    [onSlideChange]
  );

  const onBreakpoint = useCallback(
    (swiper: SwiperClass, _breakpointParams: SwiperOptions) => {
      setSlidesPerGroup(swiper.params.slidesPerGroup || 1);
    },
    []
  );

  const slidesCount = React.Children.count(children);
  const totalPages = Math.ceil(slidesCount / slidesPerGroup);
  const withControls = totalPages > 1;

  const slidesWithProps = useMemo(
    () =>
      React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const fallbackSSRStyle = swiper
            ? undefined
            : { maxWidth: `calc(100% / 3)` };
          return <SwiperSlide style={fallbackSSRStyle}>{child}</SwiperSlide>;
        }
      }),
    [children, swiper]
  );

  const isBeginning = !!swiper?.isBeginning;
  const isEnd = !!swiper?.isEnd;
  const labelsOrDefault = labels || defaultLabels;

  return (
    <React.Fragment>
      {totalPages > 0 && (
        <div
          className={clsx(styles.carousel, {
            [styles.carouselNormal]: variant == "normal",
            [styles.carouselWide]: variant == "wide",
            [styles.carouselInline]: variant == "inline",
            [styles.carouselInlineWithControls]:
              variant == "inline" && withControls,
            [styles.withMask]: withMask
          })}
          {...otherProps}
        >
          {withControls && variant === "inline" && (
            <PaginationButton
              dataContentPiece="slider_previous_button"
              arrow="left"
              disabled={isBeginning && !loop}
              onClick={prevSlide}
              aria-label={labelsOrDefault.previous}
              role="button"
              className={clsx(styles.buttonPrevInline, {
                [styles.hidden]: isBeginning
              })}
            />
          )}
          <div
            className={clsx(styles.mask, {
              [styles.leftOverflow]: withLeftOverflow
            })}
          >
            <div className={styles.sliderContainer}>
              <Swiper
                {...swiperProps}
                slideToClickedSlide
                uniqueNavElements={false}
                loop={loop}
                centerInsufficientSlides={variant !== "inline"}
                passiveListeners
                onSlideChange={internalOnSlideChange}
                onSwiper={setSwiperInstance}
                onBreakpoint={onBreakpoint}
                className={clsx({
                  [styles.hideNextSlideGroup]: hideNextSlideGroup
                })}
              >
                {slidesWithProps}
              </Swiper>
            </div>
          </div>

          {withControls && variant === "inline" && (
            <PaginationButton
              dataContentPiece="slider_next_button"
              arrow="right"
              disabled={isEnd && !loop}
              onClick={nextSlide}
              aria-label={labelsOrDefault.next}
              role="button"
              className={clsx(styles.buttonNextInline, {
                [styles.hidden]: isEnd
              })}
            />
          )}

          {withControls && variant !== "inline" && (
            <SliderControls
              variantColor={variantColor}
              currentSlideIndex={currentSlide}
              totalPages={totalPages}
              isBeginning={isBeginning && !loop}
              isEnd={isEnd && !loop}
              labels={labelsOrDefault}
              prevSlide={prevSlide}
              nextSlide={nextSlide}
            />
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export const Slider = SliderBase;
